import type { ShopPageContext } from "../types";

export const CATALOG_SORT_VALUES = [
    "recommended",
    "bestselling",
    "priceAsc",
    "priceDesc",
    "rating",
    "nameAsc",
    "newest",
] as const;

export const DEFAULT_CATALOG_PAGE_SIZE = 24;

export const CATALOG_SALE_FACET_CODE = "sale";

export const CATALOG_SALE_OPTION_VALUE = "1";

export type CatalogSortValue = typeof CATALOG_SORT_VALUES[number];

export type PublicCatalogFacetOption = {
    uuid: string;
    value: string;
    label: string;
    count: number;
    color: string | null;
    selected: boolean;
};

export type PublicCatalogFacet = {
    code: string;
    label: string;
    type: "terms" | "range";
    attributeUuid: string | null;
    attributeName: string | null;
    options: PublicCatalogFacetOption[];
    min: number | null;
    max: number | null;
    selectedMin: number | null;
    selectedMax: number | null;
};

export type CatalogFilterState = {
    attributes: Record<string, string[]>;
    manufacturers: string[];
    categories: string[];
    availability: string | null;
    saleOnly: boolean;
    priceMin: number | null;
    priceMax: number | null;
};

export type CatalogPaginationState = {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
};

export type SelectedCatalogFacetOption = {
    facet: PublicCatalogFacet;
    option: PublicCatalogFacetOption;
};

export const EMPTY_CATALOG_FILTER_STATE: CatalogFilterState = {
    attributes: {},
    manufacturers: [],
    categories: [],
    availability: null,
    saleOnly: false,
    priceMin: null,
    priceMax: null,
};

function recordOrNull(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === "object" && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function contextData(context: ShopPageContext | null | undefined): Record<string, unknown> {
    return recordOrNull(context?.data) ?? {};
}

function nestedRecord(data: Record<string, unknown>, key: string): Record<string, unknown> {
    return recordOrNull(data[key]) ?? {};
}

function stringOrNull(value: unknown): string | null {
    return typeof value === "string" && value.trim() !== "" ? value : null;
}

function numberOrNull(value: unknown): number | null {
    return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function positiveInteger(value: unknown, fallback: number): number {
    const parsed = numberOrNull(value);

    return parsed === null ? fallback : Math.max(1, Math.trunc(parsed));
}

function catalogData(context: ShopPageContext | null | undefined): Record<string, unknown> {
    return nestedRecord(contextData(context), "catalog");
}

function stringList(value: unknown): string[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value.filter((item): item is string => (
        typeof item === "string" && item.trim() !== ""
    ));
}

export function normalizeCatalogSort(value: string | null | undefined): CatalogSortValue {
    return CATALOG_SORT_VALUES.includes(value as CatalogSortValue)
        ? value as CatalogSortValue
        : "recommended";
}

export function catalogSortFromContext(
    context: ShopPageContext | null | undefined,
): CatalogSortValue {
    return normalizeCatalogSort(stringOrNull(catalogData(context).sort));
}

export function catalogQueryFromContext(
    context: ShopPageContext | null | undefined,
): string | null {
    return stringOrNull(catalogData(context).query)
        ?? stringOrNull(nestedRecord(contextData(context), "search").query);
}

export function catalogTotalFromContext(
    context: ShopPageContext | null | undefined,
    fallback = 0,
): number {
    return Math.max(0, Math.trunc(numberOrNull(catalogData(context).total) ?? fallback));
}

export function catalogPaginationFromContext(
    context: ShopPageContext | null | undefined,
): CatalogPaginationState {
    const catalog = catalogData(context);
    const total = Math.max(0, Math.trunc(numberOrNull(catalog.total) ?? 0));
    const page = positiveInteger(catalog.page, 1);
    const limit = positiveInteger(catalog.limit, DEFAULT_CATALOG_PAGE_SIZE);
    const totalPages = total === 0 ? 0 : Math.ceil(total / limit);

    return {
        total,
        page,
        limit,
        totalPages,
        hasPreviousPage: page > 1,
        hasNextPage: page < totalPages,
    };
}

export function catalogFiltersFromContext(
    context: ShopPageContext | null | undefined,
): CatalogFilterState {
    const catalog = catalogData(context);
    const rawAttributes = recordOrNull(catalog.attributeFilters) ?? {};
    const attributes = Object.fromEntries(Object.entries(rawAttributes).flatMap(([attributeUuid, rawItems]) => {
        const items = stringList(rawItems);

        return items.length > 0 ? [[attributeUuid, items]] : [];
    }));

    return {
        attributes,
        manufacturers: stringList(catalog.manufacturerUuids),
        categories: stringList(catalog.categoryFilterUuids),
        availability: stringOrNull(catalog.availability),
        saleOnly: catalog.saleOnly === true,
        priceMin: numberOrNull(catalog.priceMin),
        priceMax: numberOrNull(catalog.priceMax),
    };
}

export function catalogFacetsFromContext(
    context: ShopPageContext | null | undefined,
): PublicCatalogFacet[] {
    const value = catalogData(context).facets;

    if (!Array.isArray(value)) {
        return [];
    }

    return value.flatMap((item): PublicCatalogFacet[] => {
        const facet = recordOrNull(item);

        if (!facet) {
            return [];
        }

        const attributeUuid = stringOrNull(facet.attributeUuid);
        const attributeName = stringOrNull(facet.attributeName);
        const code = stringOrNull(facet.code) ?? (attributeUuid ? `attribute:${attributeUuid}` : null);
        const label = stringOrNull(facet.label) ?? attributeName;
        const type = stringOrNull(facet.type) === "range" ? "range" : "terms";

        if (!code || !label || !Array.isArray(facet.options)) {
            return [];
        }

        const options = facet.options.flatMap((itemOption): PublicCatalogFacetOption[] => {
            const option = recordOrNull(itemOption);

            if (!option) {
                return [];
            }

            const uuid = stringOrNull(option.uuid);
            const value = stringOrNull(option.value) ?? uuid;
            const optionLabel = stringOrNull(option.label);

            if (!uuid || !value || !optionLabel) {
                return [];
            }

            return [{
                uuid,
                value,
                label: optionLabel,
                count: numberOrNull(option.count) ?? 0,
                color: stringOrNull(option.color),
                selected: option.selected === true,
            }];
        });

        return [{
            code,
            label,
            type,
            attributeUuid,
            attributeName,
            options,
            min: numberOrNull(facet.min),
            max: numberOrNull(facet.max),
            selectedMin: numberOrNull(facet.selectedMin),
            selectedMax: numberOrNull(facet.selectedMax),
        }];
    });
}

export function catalogFacetsWithSale(
    facets: PublicCatalogFacet[],
    filters: CatalogFilterState,
    includeSale: boolean,
    saleLabel: string,
    saleOptionLabel: string,
): PublicCatalogFacet[] {
    if (!includeSale || facets.some((facet) => facet.code === CATALOG_SALE_FACET_CODE)) {
        return facets;
    }

    return [{
        code: CATALOG_SALE_FACET_CODE,
        label: saleLabel,
        type: "terms",
        attributeUuid: null,
        attributeName: null,
        options: [{
            uuid: "sale-only",
            value: CATALOG_SALE_OPTION_VALUE,
            label: saleOptionLabel,
            count: 0,
            color: null,
            selected: filters.saleOnly,
        }],
        min: null,
        max: null,
        selectedMin: null,
        selectedMax: null,
    }, ...facets];
}

function appendSortedList(params: URLSearchParams, name: string, values: string[]): void {
    for (const value of [...values].sort()) {
        params.append(name, value);
    }
}

function serializeCatalogParams(
    sort: CatalogSortValue,
    filters: CatalogFilterState,
    query: string | null | undefined,
    page?: number | null,
    limit?: number | null,
): URLSearchParams {
    const params = new URLSearchParams();
    if (query) {
        params.set("q", query);
    }

    if (sort !== "recommended") {
        params.set("sort", sort);
    }

    if (filters.priceMin !== null) {
        params.set("priceMin", String(filters.priceMin));
    }

    if (filters.priceMax !== null) {
        params.set("priceMax", String(filters.priceMax));
    }

    if (filters.availability) {
        params.set("availability", filters.availability);
    }

    if (filters.saleOnly) {
        params.set("saleOnly", "1");
    }

    appendSortedList(params, "manufacturer[]", filters.manufacturers);
    appendSortedList(params, "category[]", filters.categories);

    for (const [attributeUuid, itemUuids] of Object.entries(filters.attributes).sort(([first], [second]) => (
        first.localeCompare(second)
    ))) {
        appendSortedList(params, `attribute[${attributeUuid}][]`, itemUuids);
    }

    const normalizedPage = page === null || page === undefined ? 1 : Math.max(1, Math.trunc(page));
    const normalizedLimit = limit === null || limit === undefined
        ? DEFAULT_CATALOG_PAGE_SIZE
        : Math.max(1, Math.trunc(limit));

    if (normalizedPage > 1) {
        params.set("page", String(normalizedPage));
    }

    if (normalizedLimit !== DEFAULT_CATALOG_PAGE_SIZE) {
        params.set("limit", String(normalizedLimit));
    }

    return params;
}

function catalogHrefWithParams(baseHref: string, catalogParams: URLSearchParams): string {
    const hashIndex = baseHref.indexOf("#");
    const hash = hashIndex >= 0 ? baseHref.slice(hashIndex) : "";
    const hrefWithoutHash = hashIndex >= 0 ? baseHref.slice(0, hashIndex) : baseHref;
    const queryIndex = hrefWithoutHash.indexOf("?");
    const pathname = queryIndex >= 0 ? hrefWithoutHash.slice(0, queryIndex) : hrefWithoutHash;
    const existingParams = new URLSearchParams(queryIndex >= 0 ? hrefWithoutHash.slice(queryIndex + 1) : "");
    const catalogParameterNames = new Set([
        "q",
        "sort",
        "priceMin",
        "priceMax",
        "availability",
        "saleOnly",
        "manufacturer[]",
        "category[]",
        "page",
        "limit",
    ]);

    for (const key of Array.from(existingParams.keys())) {
        if (catalogParameterNames.has(key) || key.startsWith("attribute[")) {
            existingParams.delete(key);
        }
    }

    for (const [key, value] of catalogParams) {
        existingParams.append(key, value);
    }

    const queryString = existingParams.toString();

    return `${queryString === "" ? pathname : `${pathname}?${queryString}`}${hash}`;
}

export function catalogHref(
    baseHref: string,
    sort: CatalogSortValue,
    filters: CatalogFilterState,
    query?: string | null,
): string {
    const params = serializeCatalogParams(sort, filters, query);

    return catalogHrefWithParams(baseHref, params);
}

export function catalogPaginationHref(
    baseHref: string,
    sort: CatalogSortValue,
    filters: CatalogFilterState,
    query: string | null | undefined,
    page: number,
    limit = DEFAULT_CATALOG_PAGE_SIZE,
): string {
    const params = serializeCatalogParams(sort, filters, query, page, limit);

    return catalogHrefWithParams(baseHref, params);
}

export function activeCatalogFilterCount(
    filters: CatalogFilterState,
    includeSale = true,
): number {
    const attributeCount = Object.values(filters.attributes).reduce(
        (count, values) => count + values.length,
        0,
    );
    const priceCount = filters.priceMin !== null || filters.priceMax !== null ? 1 : 0;

    return attributeCount
        + filters.manufacturers.length
        + filters.categories.length
        + (filters.availability ? 1 : 0)
        + (filters.saleOnly && includeSale ? 1 : 0)
        + priceCount;
}

export function hasActiveCatalogFilters(filters: CatalogFilterState): boolean {
    return activeCatalogFilterCount(filters) > 0;
}

export function clearCatalogFiltersHref(
    baseHref: string,
    currentSort: CatalogSortValue,
    query: string | null | undefined,
): string {
    return catalogHref(baseHref, currentSort, EMPTY_CATALOG_FILTER_STATE, query);
}

function toggledValues(values: string[], value: string): string[] {
    const next = new Set(values);

    if (next.has(value)) {
        next.delete(value);
    } else {
        next.add(value);
    }

    return Array.from(next);
}

export function toggleCatalogFilter(
    filters: CatalogFilterState,
    facet: PublicCatalogFacet,
    option: PublicCatalogFacetOption,
): CatalogFilterState {
    if (facet.code === CATALOG_SALE_FACET_CODE) {
        return {
            ...filters,
            saleOnly: !(filters.saleOnly && option.value === CATALOG_SALE_OPTION_VALUE),
        };
    }

    if (facet.code === "manufacturer") {
        return {
            ...filters,
            manufacturers: toggledValues(filters.manufacturers, option.value),
        };
    }

    if (facet.code === "category") {
        return {
            ...filters,
            categories: toggledValues(filters.categories, option.value),
        };
    }

    if (facet.code === "availability") {
        return {
            ...filters,
            availability: filters.availability === option.value ? null : option.value,
        };
    }

    if (facet.attributeUuid === null) {
        return filters;
    }

    const nextValues = toggledValues(filters.attributes[facet.attributeUuid] ?? [], option.value);
    const attributes = { ...filters.attributes };

    if (nextValues.length === 0) {
        delete attributes[facet.attributeUuid];
    } else {
        attributes[facet.attributeUuid] = nextValues;
    }

    return { ...filters, attributes };
}

export function selectedCatalogFacetOptions(
    facets: PublicCatalogFacet[],
): SelectedCatalogFacetOption[] {
    return facets.flatMap((facet) => facet.options
        .filter((option) => option.selected)
        .map((option) => ({ facet, option })));
}

export function catalogFacetSelectedCount(facet: PublicCatalogFacet): number {
    if (facet.type === "range") {
        return facet.selectedMin !== null || facet.selectedMax !== null ? 1 : 0;
    }

    return facet.options.filter((option) => option.selected).length;
}

export function visibleCatalogFacetOptions(
    options: PublicCatalogFacetOption[],
    limit = 10,
): PublicCatalogFacetOption[] {
    const visible = options.slice(0, limit);
    const visibleUuids = new Set(visible.map((option) => option.uuid));

    for (const option of options) {
        if (option.selected && !visibleUuids.has(option.uuid)) {
            visible.push(option);
            visibleUuids.add(option.uuid);
        }
    }

    return visible;
}
